import { createFileRoute } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";

import { DayBarChart } from "~/components/day-bar-chart";
import {
  type DayCount,
  fillDateGaps,
  chartDateRange,
} from "~/lib/shared/day-chart";
import { purple } from "~/lib/ui";

export const getAppointmentsByDay = createServerFn({ method: "GET" }).handler(
  async (): Promise<Array<DayCount>> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { appointments } = await import("~/../drizzle/schema");
    const { sql, and, gte, lt } = await import("drizzle-orm");

    const { rangeStart, rangeEnd } = chartDateRange(21, 7);
    const db = await getDBWithInit();

    const rows = await db
      .select({
        date: sql<string>`DATE(${appointments.startTime})`.as("date"),
        count: sql<number>`COUNT(*)::int`.as("count"),
      })
      .from(appointments)
      .where(
        and(
          gte(appointments.startTime, rangeStart),
          lt(appointments.startTime, rangeEnd),
        ),
      )
      .groupBy(sql`DATE(${appointments.startTime})`)
      .orderBy(sql`DATE(${appointments.startTime})`);

    return fillDateGaps(rows, {
      daysBefore: 21,
      daysAfter: 7,
      pastColor: purple[500],
      futureColor: purple[300],
    });
  },
);

function AppointmentsChartPage() {
  const { data } = Route.useLoaderData();
  return (
    <DayBarChart
      data={data}
      noun="Appointments"
      rangeLabel="3 weeks ago to 1 week ahead"
    />
  );
}

export const Route = createFileRoute("/embed/appointments-chart/")({
  head: () => ({
    title: "Appointments Chart - Dream Coach Match",
    meta: [
      {
        name: "description",
        content: "Embedded chart showing scheduled appointments over time",
      },
    ],
  }),
  loader: async () => {
    const data = await getAppointmentsByDay();
    return { data };
  },
  component: AppointmentsChartPage,
});
